// The Fleet page's machine scenarios (#6559): 6, 20 and 240 machines plus an empty fleet,
// served by `GET /v1/fleet/utilization` under the mock-only `?mock_fleet=` param
// (`fleet-utilization-mocks.ts` reads it; no param keeps that file's own two-box fixture).
// They mirror the prototype's sample sets (`docs/design/fleet-redesign/prototype.html`,
// `today()`, `fleet20()`, `fleetBig()`), so a screenshot sits beside its scenario.
//
// A ROW STATES RAW READINGS, NEVER A VERDICT. Each machine is described the way a box
// reports itself (load, free memory, free workspace disk, the last five one-minute
// samples, whether it checked in) and the verdict is `machineVerdict` from
// `@developerz/domain` — the same pure function `GET /v1/fleet/utilization` calls. So a
// fixture cannot show a health the server would never produce (the prototype's own
// quirk: it calls one sample above 90% overloaded; the build needs five).
//
// Stateless. `lib/fleet-attention.test.ts` reads {@link fleetScenario} too, so the pinned
// Needs attention rows and the screenshot come off one fixture.

import { machineJobInputs, machineVerdict, machineVerdictWire } from '@developerz/domain';
import {
  AGENTS_JOBS,
  LAST_KNOWN_JOBS,
  mockHeldJobs,
  mockJobs,
  mockJobsBeat,
  OLD_RUNNER_JOBS,
  STOPPING_JOB,
  STUCK_JOB,
  THREE_JOBS,
  TWO_JOBS_ONE_STARTING,
} from './fleet-machine-jobs-mocks';
import {
  CORES,
  lastBeatMs,
  MEM_TOTAL,
  MIN,
  presenceOf,
  type Seed,
  type Status,
  stats,
} from './fleet-machine-seed';
import type { FleetUtilization, UtilBox, UtilLane } from './fleetUtilization';

/** The scenarios `?mock_fleet=` selects. */
export const FLEET_SCENARIOS = ['6', '20', '240', 'empty'] as const;
export type FleetScenario = (typeof FLEET_SCENARIOS)[number];

/**
 * A machine id shaped like the real one (`runners.id` is a uuid), its first 8 characters
 * distinct per machine: the card names a machine by its name and those 8 (#6443), and search
 * matches them. A multiplicative hash, so neighbours do not share a prefix.
 */
function scenarioId(i: number): string {
  const head = Math.imul(i, 0x9e3779b1) >>> 0;
  return `${head.toString(16).padStart(8, '0')}-0000-4000-8000-${String(i).padStart(12, '0')}`;
}

/** One machine row, its verdict decided by the domain from the seed's readings. */
export function machineRow(i: number, s: Seed, nowMs: number): UtilBox {
  const live = s.status === 'working' || s.status === 'idle' || s.status === 'paused';
  const st = stats(s);
  const verdict = machineVerdict({
    nowMs,
    enrollment: s.status === 'waiting' ? 'pending' : 'enrolled',
    drained: s.status === 'paused',
    enrolledAtMs: s.status === 'starting' ? nowMs - MIN : nowMs - 30 * 24 * 60 * MIN,
    createdAtMs: s.status === 'starting' ? nowMs - MIN : nowMs - 30 * 24 * 60 * MIN,
    lastHeartbeatAtMs: lastBeatMs(s, live, nowMs),
    presence: presenceOf(s, live, st),
    specs: { cpuCores: CORES, memBytes: MEM_TOTAL },
    samples: live
      ? [0, 1, 2, 3, 4].map((k) => ({
          sampledAtMs: nowMs - 20_000 - k * MIN,
          loadAvg1: st.loadAvg1,
          cpuCores: CORES,
        }))
      : [],
    jobs: machineJobInputs(mockHeldJobs(i, s, nowMs), mockJobsBeat(i, s, live)),
  });
  const id = scenarioId(i);
  return {
    id,
    display_name: s.name,
    hostname: live ? s.name : null,
    owner_kind: 'org',
    earmark: null,
    mode: 'worker',
    online: live,
    active_sessions: s.status === 'working' ? 1 : 0,
    current_class: s.status === 'working' ? 'coding' : null,
    stats: live ? st : null,
    last_beat_iso: live ? new Date(nowMs - 20_000).toISOString() : null,
    runner_version: '0.8.0',
    drained_iso: s.status === 'paused' ? new Date(nowMs - 60 * MIN).toISOString() : null,
    specs: { cpu_cores: CORES, mem_bytes: MEM_TOTAL },
    server: { key: `label:${s.server}`, label: s.server, address: null },
    via_org: s.viaOrg ?? null,
    max_slots: 4,
    // The move budget (#6768): a fresh window, every move left.
    moves: { left: 4, used: 0, free_at: null },
    enrolled_iso: new Date(nowMs - 30 * 24 * 60 * MIN).toISOString(),
    owner: { kind: 'org', label: s.viaOrg ?? 'developerz-ai' },
    jobs: mockJobs(i, s, nowMs),
    // A live machine on a runner new enough to report per-job usage (#6743), unless the
    // seed says it is older.
    reports_job_usage: live && s.reportsJobUsage !== false,
    verdict: machineVerdictWire(verdict),
  };
}

const seed = (
  name: string,
  server: string,
  status: Status,
  cpu: number,
  memGb: number,
  diskUsedGb: number,
  extra: Partial<Seed> = {},
): Seed => ({ name, server, status, cpu, memGb, diskUsedGb, ...extra });

/** The prototype's "Today": four overloaded on one server, one of them failing clean-up. */
const TODAY: readonly Seed[] = [
  seed('agents', 'hv-1', 'working', 240, 5.9, 26.1, {
    diskTotalGb: 206.9,
    cleanupErrors: 8,
    jobs: AGENTS_JOBS,
  }),
  seed('fleet-5', 'hv-1', 'working', 170, 2.4, 30.0, { taskId: 'tsk_mock_live' }),
  seed('fleet-4', 'hv-1', 'working', 120, 1.2, 36.9, { taskId: 'tsk_mock_done' }),
  seed('fleet-2', 'hv-1', 'working', 105, 2.3, 31.6, { taskId: 'tsk_mock_parked' }),
  seed('fleet-3', 'hv-1', 'working', 22, 1.8, 28.1),
  seed('fleet-1', 'hv-1', 'working', 9, 1.0, 28.7),
];

/** The prototype's "20 machines", two servers. */
const TWENTY: readonly Seed[] = (
  [
    ['agents', 'hv-1', 'working', 135, 5.2, 26.1, 206.9],
    ['fleet-1', 'hv-1', 'working', 112, 2.1, 30.2],
    ['fleet-2', 'hv-1', 'working', 84, 3.0, 29.5],
    ['fleet-3', 'hv-1', 'working', 61, 2.6, 33.0],
    ['fleet-4', 'hv-1', 'working', 55, 1.9, 50.8],
    ['fleet-5', 'hv-1', 'working', 48, 2.2, 31.1],
    ['fleet-6', 'hv-1', 'working', 44, 4.1, 27.0],
    ['Frontend builds', 'hv-1', 'working', 39, 1.4, 35.2],
    ['fleet-8', 'hv-1', 'working', 36, 2.0, 28.8],
    ['fleet-9', 'hv-1', 'idle', 4, 1.0, 26.4],
    ['fleet-10', 'hv-1', 'idle', 3, 0.9, 25.1],
    ['fleet-11', 'hv-1', 'paused', 2, 0.8, 30.0],
    ['fleet-12', 'hv-2', 'working', 58, 3.3, 34.6],
    ['fleet-13', 'hv-2', 'working', 52, 2.7, 29.9],
    ['fleet-14', 'hv-2', 'working', 47, 6.0, 40.2],
    ['fleet-15', 'hv-2', 'working', 41, 2.2, 31.7],
    ['fleet-16', 'hv-2', 'working', 33, 1.8, 27.3],
    ['fleet-17', 'hv-2', 'working', 29, 13.2, 30.5],
    ['fleet-18', 'hv-2', 'idle', 5, 1.1, 24.9],
    ['fleet-19', 'hv-2', 'not_responding', 0, 2.4, 33.8],
  ] as const
).map(([n, sv, st, cpu, mem, du, dt]) =>
  seed(n, sv, st, cpu, mem, du, {
    ...(dt === undefined ? {} : { diskTotalGb: dt }),
    // One of the viewer's private machines from another org, so the set shows "From <org>".
    ...(n === 'fleet-18' ? { viaOrg: 'acme-labs' } : {}),
    // A Blocked and a Needs you card (#6785), through the open mock blockers' tasks.
    ...(n === 'fleet-2' ? { taskId: 'tsk_mock_live' } : {}),
    ...(n === 'fleet-3' ? { taskId: 'tsk_mock_done' } : {}),
    // Three Needs you cards asked in prose, one per answer arm (#6726): Answer here.
    ...(n === 'fleet-4' ? { taskId: 'tsk_mock_parked' } : {}),
    ...(n === 'fleet-5' ? { taskId: 'tsk_mock_held' } : {}),
    ...(n === 'fleet-6' ? { taskId: 'tsk_mock_linked' } : {}),
    // The jobs list (#6745): two jobs with one Starting, three in two groups, a runner too
    // old to report per-job usage, and a silent machine's last-known jobs.
    ...(n === 'fleet-1' ? { jobs: TWO_JOBS_ONE_STARTING } : {}),
    ...(n === 'fleet-8' ? { jobs: THREE_JOBS } : {}),
    ...(n === 'fleet-12' ? { jobs: OLD_RUNNER_JOBS, reportsJobUsage: false } : {}),
    ...(n === 'fleet-19' ? { jobs: LAST_KNOWN_JOBS } : {}),
    // #6746: a job silent for over an hour (Needs attention, "Job stuck") and one told to
    // stop whose box has not ended it (the jobs state `stopping`, no row).
    ...(n === 'fleet-16' ? { jobs: STUCK_JOB } : {}),
    ...(n === 'fleet-15' ? { jobs: STOPPING_JOB } : {}),
  }),
);

/** The prototype's hand-placed problems in its 240-machine set, by machine number. */
const BIG_NAMES: Readonly<Record<number, string>> = {
  3: 'Frontend builds',
  47: 'Billing work',
  120: 'Night shift',
  181: 'Docs bot',
};
const BIG_OVERRIDES: Readonly<Record<number, Partial<Seed>>> = {
  12: { cleanupErrors: 2 },
  37: { diskUsedGb: 57.9 },
  61: { status: 'not_responding' },
  88: { diskUsedGb: 50.1 },
  142: { memGb: 15.6 },
  175: { status: 'not_responding' },
  203: { memGb: 13.1 },
  219: { status: 'offline' },
};

/** One machine of the 240, drawing from the prototype's generator in its order. */
function bigSeed(n: number, rnd: () => number): Seed {
  const server = `hv-${Math.ceil(n / 30)}`;
  const r = rnd();
  const status: Status = r < 0.15 ? 'idle' : r < 0.18 ? 'paused' : 'working';
  const working = status === 'working';
  let cpu = working ? Math.round(20 + rnd() * 48) : Math.round(2 + rnd() * 6);
  if (server === 'hv-5' && working && rnd() < 0.42) cpu = Math.round(105 + rnd() * 90);
  else if (working && rnd() < 0.07) cpu = Math.round(72 + rnd() * 16);
  const memGb = Math.round((1 + rnd() * 8) * 10) / 10;
  const diskUsedGb = Math.round((20 + rnd() * 22) * 10) / 10;
  rnd(); // the prototype's last-seen draw, kept so every later machine draws the same values
  const name = BIG_NAMES[n] ?? `fleet-${n}`;
  return { ...seed(name, server, status, cpu, memGb, diskUsedGb), ...BIG_OVERRIDES[n] };
}

/** The prototype's 240 machines on 8 servers, from its own seeded generator. */
function big(): readonly Seed[] {
  let s = 19;
  const rnd = (): number => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return Array.from({ length: 240 }, (_, i) => bigSeed(i + 1, rnd));
}

const SEEDS: Readonly<Record<FleetScenario, () => readonly Seed[]>> = {
  '6': () => TODAY,
  '20': () => TWENTY,
  '240': big,
  empty: () => [],
};

/** Pending work per class: the prototype's "Waiting in line" (168 / 23 / 412 / 0). */
const WAITING: Readonly<Record<FleetScenario, number>> = {
  '6': 168,
  '20': 23,
  '240': 412,
  empty: 0,
};

function lanes(total: number): readonly UtilLane[] {
  const ci = Math.floor(total / 4);
  return [
    { class: 'interactive', depth: 0 },
    { class: 'coding', depth: total - ci },
    { class: 'ci', depth: ci },
    { class: 'gardener', depth: 0 },
  ];
}

/** One scenario's whole utilization body. */
export function fleetScenario(name: FleetScenario, nowMs: number): FleetUtilization {
  return {
    load: { live_sessions: 0, burn: 0 },
    boxes: SEEDS[name]().map((s, i) => machineRow(i + 1, s, nowMs)),
    keys: [],
    keys_status: { usable: 0, cooling: 0, subs_exhausted: false },
    lanes: lanes(WAITING[name]),
  };
}

/** The scenario `?mock_fleet=` names, or null when the param is absent or unknown. */
export function requestedScenario(search: string): FleetScenario | null {
  const asked = new URLSearchParams(search).get('mock_fleet');
  return FLEET_SCENARIOS.find((s) => s === asked) ?? null;
}
